import { useEffect, useMemo, useState } from 'react';
import {
  Navigate,
  Outlet,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import type { User } from '@supabase/supabase-js';
import AdminLayout from '@/components/layout/AdminLayout';
import { supabase } from '@/lib/supabase/client';

type AuthStatus = 'loading' | 'authenticated' | 'redirect';

const AdminRoute = () => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<User | null>(null);
  const [redirectPath, setRedirectPath] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const loadSession = async () => {
      try {
        const { data, error } = await supabase.auth.getSession();

        if (!isMounted) {
          return;
        }

        if (error) {
          console.error('AdminRoute: session fetch failed', error);
          setStatus('redirect');
          return;
        }

        if (!data.session) {
          setStatus('redirect');
          return;
        }

        setUser(data.session.user);
        setStatus('authenticated');
      } catch (err) {
        console.error('AdminRoute: unexpected error', err);
        if (isMounted) {
          setStatus('redirect');
        }
      }
    };

    loadSession();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
    } catch (error) {
      console.error('AdminRoute: sign out failed', error);
    } finally {
      setUser(null);
      setRedirectPath('/admin/login');
      setStatus('redirect');
    }
  };

  const destination = useMemo(() => {
    if (redirectPath) {
      return redirectPath;
    }

    return '/admin/login';
  }, [redirectPath]);

  if (status === 'redirect') {
    return (
      <Navigate
        to={destination}
        replace
        state={{
          from: location.pathname,
          type: navigationType,
        }}
      />
    );
  }

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-elev8-dark text-white">
        <div className="w-16 h-16 border-4 border-t-elev8-blue border-white/30 rounded-full animate-spin mb-4" />
        <p className="text-sm text-white/70">Checking your admin session…</p>
      </div>
    );
  }

  return (
    <AdminLayout user={user} onSignOut={handleSignOut}>
      <Outlet />
    </AdminLayout>
  );
};

export default AdminRoute;
